import { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import capitalize from 'lodash/capitalize'

import { generateColorTokensV2, invertHexColor, MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, cn, ColorPickerV2, IColor, Modal } from '@makyoapp/makyo-ui'

import { toasted } from '@/lib/toast'
import { useAppBrandKitContext } from '@/context/AppBrandKitContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { colorToSource, ExtendedColor, sourceToHex } from '@/shared/brand-kit'
import { MAX_EXTENDED_COLORS } from '@/shared/constants'
import { EVENT_KEYS } from '@/shared/custom-event'
import { ColorTokensDemo } from '@/components/BrandKit/ColorTokensDemo'

interface ExtendedColorBoxProps {
  isAdd?: boolean
  extended: ExtendedColor
  onSave: (color: IColor) => Promise<void>
  onRemove?: () => Promise<void>
}

const ExtendedColorBox = ({ extended, onSave, isAdd, onRemove }: ExtendedColorBoxProps) => {
  const [hex, setHex] = useState(extended.hex)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (extended.hex !== hex) {
      setHex(extended.hex)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extended.hex])

  const handleCancel = () => {
    setHex(extended.hex)
  }

  const handleSave = async (color: IColor) => {
    setIsLoading(true)
    await onSave(color)
    setIsLoading(false)
  }

  const handleRemove = async () => {
    setIsLoading(true)
    await onRemove?.()
    setIsLoading(false)
  }

  return (
    <ColorPickerV2
      color={hex}
      onChange={(color) => setHex(color.hex)}
      onCancel={handleCancel}
      onSave={handleSave}
      renderToggle={() =>
        isAdd ? (
          <div
            title='Add Color'
            key={extended.label}
            role='button'
            className={cn(
              'cursor-pointer w-full aspect-square overflow-hidden truncate rounded-md mb-1 p-2 text-center border flex-cc',
              'mk-bdr-hover justify-center '
            )}
          >
            {isLoading ? (
              <span className='loading spin' />
            ) : (
              <div className='flex-1 flex items-center justify-center'>
                <MaterialIcon icon='add' size={20} />
              </div>
            )}
          </div>
        ) : (
          <div
            key={extended.label}
            role='button'
            className={cn(
              'group relative cursor-pointer w-full aspect-square rounded-md mb-1 p-2 text-center flex-cc border bdr',
              'hover:opacity-90 hover:border-gray-500 dark:hover:border-gray-400'
            )}
            style={{ backgroundColor: hex, color: invertHexColor(hex) }}
          >
            {isLoading ? (
              <span className='loading spin' />
            ) : (
              <div className='hidden group-hover:flex flex-1 items-center justify-center'>
                <MaterialIcon icon='colorize' size={20} />
              </div>
            )}

            {onRemove && (
              <div
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemove()
                }}
                className='absolute top-[-3px] right-[-3px] p-0.5 size-4 rounded-full flex-cc bg-red-500 text-white'
              >
                <MaterialIcon icon='close' size={14} />
              </div>
            )}
          </div>
        )
      }
      modal
      dropdownPickerProps={{
        side: 'right',
      }}
    />
  )
}

const ColorBox = ({ color, label, token }: { color: string; label: string; token: any }) => {
  return (
    <div
      className='w-full aspect-square flex items-center justify-center text-center p-2.5 border bdr rounded-md'
      style={{
        backgroundColor: `var(--color-${token})`,
        color: `var(--color-on-${token})`,
      }}
    >
      <div>
        <p className='text-m-sm font-medium'>{capitalize(label)}</p>
        <p className='text-m-sm'>{color.toUpperCase()}</p>
      </div>
    </div>
  )
}

export function BrandKitColorEditor() {
  const { t } = useTranslation()

  // access app theme mode (light/dark)
  const [colorScheme] = useBuilderColorSchemeV2()
  const mode = colorScheme.current

  // access app brand kit context
  const {
    currentTheme,
    activeBrandKitID,
    activeBrandKit,
    activeColorThemeIndex,
    brandkitExtendedColors,
    handleUpdateBrandKit,
    handleUpdateActiveBrandKit,
  } = useAppBrandKitContext()

  const theme = currentTheme.theme
  const sourceColor = sourceToHex(theme.source)

  // local state
  const [openViewColorTokens, setOpenViewColorTokens] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // generate color tokens
  const { palettes, sourceInfo } = generateColorTokensV2(sourceColor, mode)

  const modeIndex = mode === 'light' ? 0 : 1
  const primary50Color = palettes.primary[50][modeIndex]
  const secondary50Color = palettes.secondary[50][modeIndex]
  const tertiary50Color = palettes.tertiary[50][modeIndex]
  const neutral70Color = palettes.neutral[70][modeIndex]

  const updateSourceColor = async (color: string | IColor) => {
    if (!activeBrandKit) return

    setIsLoading(true)

    const argbColor = colorToSource(color)

    const newBrandKit = { ...activeBrandKit }
    const idx = Number(activeColorThemeIndex)
    newBrandKit.colors[idx].value = argbColor

    try {
      await handleUpdateBrandKit(newBrandKit)
      window.dispatchEvent(new CustomEvent(EVENT_KEYS.RELOAD_PREVIEW_FRAME))
      toasted(t('Source colors updated'))
    } catch (err) {
      console.error('Error updating source color:', err)
      toasted(t('Failed to update source color'), { type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  const updateExtendeColor = useCallback(
    async (color: string | IColor, index: number) => {
      if (!activeBrandKitID) return

      try {
        await handleUpdateActiveBrandKit((prev) => {
          const argbColor = colorToSource(color)

          let prevExtendedColors = prev._Colors[0]._CustomColors

          if (!prevExtendedColors[index]) {
            prevExtendedColors[index] = {
              _Name: `Extended ${index + 1}`,
              _Value: argbColor,
              _Blend: false,
            }
          } else {
            // validate if name is matched the index + 1
            const expectedName = `Extended ${index + 1}`
            if (prevExtendedColors[index]._Name !== expectedName) {
              prevExtendedColors[index]._Name = expectedName
            }
            prevExtendedColors[index]._Value = argbColor
          }

          return prev
        })

        toasted('Extended colors updated')
      } catch (err) {
        console.error('Error updating extended color:', err)
        toasted('Failed to update extended color', { type: 'error' })
      }
    },
    [activeBrandKitID, handleUpdateActiveBrandKit]
  )

  const removeExtendedColor = async (index: number) => {
    if (!activeBrandKitID) return

    try {
      await handleUpdateActiveBrandKit((prev) => {
        let prevExtendedColors = prev._Colors[0]._CustomColors
        prevExtendedColors.splice(index, 1)
        return prev
      })

      toasted(t('Extended colors updated'))
    } catch (err) {
      console.error('Error updating extended color:', err)
      toasted(t('Failed to update extended color'), { type: 'error' })
    }
  }

  return (
    <>
      <div className='flex flex-col gap-4'>
        {/* source color */}
        <div id='source-color-box-container'>
          <div className='text-light font-medium'>
            <Trans>Source Color</Trans>
          </div>

          <div className='mt-2 flex p-4 border rounded-md bdr w-full'>
            <ColorPickerV2
              color={sourceColor}
              onSave={(color) => {
                updateSourceColor(color)
              }}
              dropdownPickerProps={{
                side: 'right',
                sideOffset: 8,
                alignOffset: -1,
              }}
              renderToggle={(currentColor) => (
                <div
                  className={cn(
                    'group mr-2 relative cursor-pointer w-10 aspect-square rounded-md mb-1 p-2 text-center flex-cc border bdr',
                    'hover:opacity-90 hover:border-gray-500 dark:hover:border-gray-400'
                  )}
                  style={{ background: currentColor.hex, color: invertHexColor(currentColor.hex) }}
                >
                  {isLoading ? (
                    <div className='animate-show absolute inset-0 flex items-center justify-center rounded-md bg-black/50'>
                      <div className='loading spin' />
                    </div>
                  ) : (
                    <div className='hidden group-hover:flex flex-1 items-center justify-center'>
                      <MaterialIcon icon='colorize' size={18} />
                    </div>
                  )}
                </div>
              )}
              disableAlpha
            />
            <div className='flex-cc'>
              <div>{sourceColor.toUpperCase()}</div>
            </div>
          </div>
        </div>

        {/* brand colors */}
        <div>
          <div className='text-light font-medium'>
            <Trans>Brand Colors</Trans>
          </div>
          <div className='mt-2 grid grid-cols-4 gap-2'>
            <ColorBox color={primary50Color} token='primary-50' label='Primary' />
            <ColorBox color={secondary50Color} token='secondary-50' label='Secondary' />
            <ColorBox color={tertiary50Color} token='tertiary-50' label='Tertiary' />
            <ColorBox color={neutral70Color} token='neutral-70' label='Neutral' />
          </div>
        </div>

        {/* extended colors */}
        {brandkitExtendedColors.length > 0 && (
          <div>
            <div className='text-light font-medium'>
              <Trans>Extended Colors</Trans>
            </div>
            <div className='mt-2 grid grid-cols-6 gap-2'>
              {brandkitExtendedColors.map((c, idx) => (
                <ExtendedColorBox
                  key={idx}
                  extended={c}
                  onSave={async (color) => {
                    await updateExtendeColor(color, idx)
                  }}
                  // onRemove={async () => {
                  //   await removeExtendedColor(idx)
                  // }}
                />
              ))}

              {brandkitExtendedColors.length < MAX_EXTENDED_COLORS && (
                <ExtendedColorBox
                  isAdd
                  extended={{ label: 'Add Color', hex: '#ffffff', argb: 0 }}
                  onSave={async (color) => {
                    await updateExtendeColor(color, brandkitExtendedColors.length)
                  }}
                />
              )}
            </div>
          </div>
        )}

        <div className='w-full'>
          <Button
            className='w-full'
            onClick={() => setOpenViewColorTokens(true)}
            icon={<MaterialIcon icon='palette' size={18} />}
          >
            View Color Tokens
          </Button>
        </div>
      </div>

      {openViewColorTokens && (
        <Modal show={openViewColorTokens} onHide={() => setOpenViewColorTokens(false)}>
          <Modal.Header onCloseClick={() => setOpenViewColorTokens(false)}>
            <Trans>Color Tokens</Trans>
          </Modal.Header>
          <Modal.Body className='modal-lg max-h-[90vh] overflow-y-auto'>
            <ColorTokensDemo
              source={theme.source}
              extendedColors={brandkitExtendedColors}
              mode={mode}
              onUpdateSourceColor={updateSourceColor}
              onUpdateExtendedColor={updateExtendeColor}
              onRemoveExtendedColor={removeExtendedColor}
              isLoading={isLoading}
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  )
}
